import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const ROSTER_TIME_ZONE = 'Asia/Shanghai';

interface ShiftAssignmentLike {
    employee_id: number;
    date: string;
    shift_id: number;
    start_time?: string | null;
    end_time?: string | null;
    attendance_kind?: string | null;
    plan_type?: string | null;
    nominal_hours?: number | null;
}

interface OperationLike {
    planned_start?: string | null;
    planned_end?: string | null;
}

interface PersistedAssignmentLike extends OperationLike {
    operation_plan_id: number;
    position_number: number;
    employee_id: number;
    date?: string | null;
    shift_id?: number | null;
}

export interface AssignmentRosterContext {
    date: string;
    shift_id: number;
}

const timePart = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const match = String(value).match(/(\d{2}):(\d{2})(?::\d{2})?/);
    return match ? `${match[1]}:${match[2]}` : null;
};

const isWorkShift = (shift: ShiftAssignmentLike): boolean => {
    const attendanceKind = String(shift.attendance_kind || shift.plan_type || '')
        .trim()
        .toUpperCase();
    if (attendanceKind) return attendanceKind === 'WORK';
    return Number(shift.nominal_hours || 0) > 0;
};

const parseRosterDatetime = (value: string) => (
    /(Z|[+-]\d{2}:\d{2})$/i.test(value)
        ? dayjs(value).tz(ROSTER_TIME_ZONE)
        : dayjs.tz(value, ROSTER_TIME_ZONE)
);

/**
 * Resolve the roster shift that physically covers an operation.
 *
 * A cross-midnight operation tail belongs to the prior roster date's night
 * shift, not to the next calendar day's REST/PAID_LEAVE plan.
 */
export const resolveOperationRosterShift = (
    employeeId: number,
    operation: OperationLike,
    shiftAssignments: ShiftAssignmentLike[],
): ShiftAssignmentLike | null => {
    if (!operation.planned_start || !operation.planned_end) return null;
    const operationStart = parseRosterDatetime(operation.planned_start);
    const operationEnd = parseRosterDatetime(operation.planned_end);
    if (!operationStart.isValid() || !operationEnd.isValid()) return null;

    const candidates = shiftAssignments
        .filter((shift) => shift.employee_id === employeeId && isWorkShift(shift))
        .map((shift) => {
            const startTime = timePart(shift.start_time);
            const endTime = timePart(shift.end_time);
            if (!startTime || !endTime) return null;

            const start = dayjs.tz(`${shift.date}T${startTime}:00`, ROSTER_TIME_ZONE);
            let end = dayjs.tz(`${shift.date}T${endTime}:00`, ROSTER_TIME_ZONE);
            if (!start.isValid() || !end.isValid()) return null;
            if (!end.isAfter(start)) end = end.add(1, 'day');
            if (operationStart.isBefore(start) || operationEnd.isAfter(end)) return null;
            return { shift, duration: end.diff(start) };
        })
        .filter((candidate): candidate is { shift: ShiftAssignmentLike; duration: number } => candidate !== null)
        .sort((a, b) => a.duration - b.duration);

    return candidates[0]?.shift || null;
};

/**
 * Resolve the roster context used by a manual assignment override.
 *
 * The result screen can display an operation's database planning window while
 * the solver assignment carries a more precise execution window. Prefer that
 * assignment-specific window, then preserve the solver-provided roster context
 * only while the same employee/date/shift plan still exists in the edited
 * roster. This avoids false null contexts without accepting a stale context
 * after a manual shift change.
 */
export const resolveAssignmentRosterContext = (
    employeeId: number,
    operationPlanId: number,
    positionNumber: number,
    operation: OperationLike,
    shiftAssignments: ShiftAssignmentLike[],
    persistedAssignments: PersistedAssignmentLike[],
): AssignmentRosterContext | null => {
    const persistedAssignment = persistedAssignments.find((assignment) => (
        assignment.operation_plan_id === operationPlanId
        && assignment.position_number === positionNumber
        && assignment.employee_id === employeeId
    ));
    const physicalShift = resolveOperationRosterShift(
        employeeId,
        persistedAssignment || operation,
        shiftAssignments,
    );
    if (physicalShift) {
        return {
            date: physicalShift.date,
            shift_id: physicalShift.shift_id,
        };
    }

    const persistedDate = persistedAssignment?.date == null
        ? null
        : String(persistedAssignment.date).slice(0, 10);
    const persistedShiftId = persistedAssignment?.shift_id == null
        ? null
        : Number(persistedAssignment.shift_id);
    if (
        persistedDate
        && Number.isInteger(persistedShiftId)
        && persistedShiftId! > 0
        && shiftAssignments.some((shift) => (
            shift.employee_id === employeeId
            && shift.date === persistedDate
            && shift.shift_id === persistedShiftId
            && isWorkShift(shift)
        ))
    ) {
        return {
            date: persistedDate,
            shift_id: persistedShiftId!,
        };
    }

    return null;
};
